import { createSlice } from '@reduxjs/toolkit';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { createControl } from '../../../../../domain/validation/validation';
import { default as cloneDeep } from 'lodash/cloneDeep';
import { IIndexable } from '../../../../../domain/interfaces';
import { USA_ID } from '../../../../../domain/addresses/addresses-provider';
import { isCountryRequired, isShippingStateRequired } from '../../domain/shopping-cart-validations';
import { restoreExtraCartDataFromStorage, saveExtraCartDataToStorage } from '../cart-storage-provider';
import { IShippingOption } from '../../interfaces';
import { getInitialCartData } from '../data-provider';

interface IShippingAddressState extends  IIndexable{
    isShippingDifferentFromBilling: boolean,

    // controls ----------------
    shipToControl: IFormControl,
    addressLine1Control: IFormControl,
    addressLine2Control: IFormControl,
    countryIdControl: IFormControl,
    stateIdControl: IFormControl,
    cityControl: IFormControl,
    zipCodeControl: IFormControl,
    phoneControl: IFormControl,
    phoneCountryIdControl: IFormControl,

    // UPS shipping
    upsShippingOptions: IShippingOption[],
    upsShippingCodeControl: IFormControl,
    upsShippingError: string,
}

const initialCartData = getInitialCartData();
const shippingAddress = initialCartData ? initialCartData.shippingAddress : null;

const extraCartData = restoreExtraCartDataFromStorage();

/**
 * initial state
 */
const initialState: IShippingAddressState = {

    isShippingDifferentFromBilling: extraCartData ? !!extraCartData.isShippingDifferentFromBilling : false,

    // ---------- CONTROLS --------------

    shipToControl: createControl('shipTo', shippingAddress?.addressPersonalName || '', {
        required: true,
        maxlength: 100,
    }),
    addressLine1Control: createControl('shippingAddressLine1', shippingAddress?.addressLine1 || '', {
        required: true,
        maxlength: 100,
    }),
    addressLine2Control: createControl('shippingAddressLine2', shippingAddress?.addressLine2 || '', {
        maxlength: 100,
    }),

    countryIdControl: createControl(
        'shippingCountryId',
        Number(shippingAddress?.addressCountryID) || 0,
        {
            required:  (control) => {
                return isCountryRequired(control);
            }
        }
    ),

    stateIdControl: createControl(
        'shippingStateId',
        Number(shippingAddress?.addressStateID) || 0,
        {
            /**
             * the state is required when selected country has states
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isShippingStateRequired(control);
            }
        }
    ),

    cityControl: createControl('shippingCity', shippingAddress?.addressCity || '', {
        required: true,
        maxlength: 100,
    }),
    zipCodeControl: createControl('shippingZipCode', shippingAddress?.addressZip || '', {
        maxlength: 10,
    }),
    phoneControl: createControl('shippingPhone', shippingAddress?.phoneNumber || '', {
        required: true,

        phone: true,
        phoneMessage: 'Please enter a valid number, use digits only.',

        minlength: 5,
        minlengthMessage: 'Please enter at least 5 digits.',

        maxlength: 12,
    }),
    phoneCountryIdControl: createControl('shippingCountryId', Number(shippingAddress?.phoneCountryID) || USA_ID, {
        required: true,
        int: true
    }),

    // ups shipping ------------
    upsShippingCodeControl: createControl('upsShippingCode', '', {
        required: true,
    }),
    upsShippingOptions: [],
    upsShippingError: '',
};

const shippingAddressSlice = createSlice({
    name: 'shippingAddress',
    initialState,
    reducers: {
        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        setControl(state, action) {

            return {
                ...state,
                [action.payload.controlName]: cloneDeep(action.payload.control),
            }
        },

        setControlsList(state, action) {

            const updated = {...state};
            const controls = action.payload.controls;

            for(let i=0; i<controls.length; i++){
                const current = controls[i];
                updated[current.controlName] = cloneDeep(current.control);
            }

            return updated;
        },

        setIsShippingDifferentFromBilling(state, action) {
            const updated = {...state};
            updated.isShippingDifferentFromBilling = !!action.payload;

            // save to extra storage
            const extra = restoreExtraCartDataFromStorage() || {};
            extra.isShippingDifferentFromBilling = updated.isShippingDifferentFromBilling;
            saveExtraCartDataToStorage(extra);

            return updated;
        }
    }
});

export default shippingAddressSlice;